'use strict';

import { Combobox } from '@reach/combobox';
import '@reach/combobox/styles.css';
import env from 'app/env';
import { createImageProxyUrl } from 'app/utils/svtimage';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import syntolkat from 'public/static/images/accessibility/syntolkat_100x56.png';
import teckensprak from 'public/static/images/accessibility/teckentolkat_100x56.png';
import tydligaretal from 'public/static/images/accessibility/tydligaretal_100x56.png';
import upplasttext from 'public/static/images/accessibility/upplasttext_100x56.png';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useQuery } from 'urql';
import { trackCustomEvent } from '../../../utils/statistics/clientStatistics';
import { listSearchEvent, searchQueryEvent } from '../../../utils/statistics/customEvents';
import SEARCH_QUERY from './AutoComplete.graphql';
import {
    Form,
    Heading,
    SearchInput,
    SearchLabel,
    SearchSuggestionEpisodeText,
    SearchSuggestionLink,
    SearchSuggestionLinkText,
    SearchSuggestionList,
    SearchSuggestionListItem,
    SearchSuggestionThumbnail,
    ShowAllListItem,
    ShowSearchResultButton,
    SubmitButton,
    SuggestionsPopOver,
    ThumbNailImage,
    SearchSuggestionTextWrapper,
} from './AutoCompleteResult.styles';
import useDebounce from 'app/hooks/useDebounce';
import PlayBadge, { PLAY_BADGE_SIZE } from '../../../ui-components/PlayBadge/PlayBadge';

const INPUT_SEARCH_PLACEHOLDER_TEXT = 'Sök på SVT Play';

const AutoCompleteButton = ({ searchTerm, onClick }) => {
    return (
        <ShowAllListItem>
            <ShowSearchResultButton
                to="/sok"
                query={{ q: searchTerm }}
                onClick={onClick}
                regTestSpec="search-result-btn"
            >
                <span className="play_search-suggestions__show-all-icon" aria-hidden="true" />
                Visa alla sökträffar
            </ShowSearchResultButton>
        </ShowAllListItem>
    );
};

const AutoCompleteLink = ({
    url,
    thumbnail,
    thumbnailAltText,
    onClick,
    linkIndex,
    totalSize,
    svtId,
    isCategory,
    category,
    title,
    episodeTitle,
    badge,
}) => {
    const stats = {
        svtId: svtId,
        position: linkIndex + 1,
        total: totalSize,
        type: category,
    };

    return (
        <SearchSuggestionLink
            to={url}
            onMouseDown={(e) => onClick(url, stats, e)}
            regTestSpec="auto-complete-link"
            isCategory={isCategory}
        >
            {!isCategory && (
                <SearchSuggestionThumbnail>
                    {badge && (
                        <PlayBadge
                            text={badge.text}
                            theme={badge.type}
                            accessibilityText={badge.altText}
                            overlay
                            size={PLAY_BADGE_SIZE.SMALL}
                        />
                    )}
                    <ThumbNailImage key={thumbnail} src={thumbnail} alt={thumbnailAltText} />
                </SearchSuggestionThumbnail>
            )}
            <SearchSuggestionTextWrapper>
                <SearchSuggestionLinkText className="linkText">{title}</SearchSuggestionLinkText>
                <SearchSuggestionEpisodeText>{episodeTitle}</SearchSuggestionEpisodeText>
            </SearchSuggestionTextWrapper>
        </SearchSuggestionLink>
    );
};

const value = (item) => {
    if (item.isCategory) {
        return `${item.title} (kategori)`;
    }
    if (item.episodeTitle) {
        return `${item.title}: ${item.episodeTitle}`;
    }

    return item.title;
};

const PopOver = ({ totalSize, result, suggestionClick, term, onAllMatchesClick }) => {
    return (
        <SuggestionsPopOver portal={false}>
            <SearchSuggestionList>
                {result.map((item) => {
                    return (
                        <Fragment key={item.svtId || item.id || item.title}>
                            {item.results.length > 0 && <Heading>{item.title}</Heading>}
                            <SearchSuggestionList>
                                {item.results.map((item, index) => {
                                    return (
                                        <SearchSuggestionListItem key={item.svtId} value={value(item)}>
                                            <AutoCompleteLink
                                                url={item.url}
                                                thumbnail={item.thumbnail}
                                                thumbnailAltText={item.thumbnailAltText}
                                                title={item.title}
                                                episodeTitle={item.episodeTitle}
                                                onClick={suggestionClick}
                                                openArchive={item.openArchive}
                                                linkIndex={index}
                                                totalSize={totalSize}
                                                svtId={item.svtId}
                                                isCategory={item.isCategory}
                                                category={item.category}
                                                badge={item.badge}
                                            />
                                        </SearchSuggestionListItem>
                                    );
                                })}
                            </SearchSuggestionList>
                        </Fragment>
                    );
                })}
                <AutoCompleteButton searchTerm={term} onClick={onAllMatchesClick} />
            </SearchSuggestionList>
        </SuggestionsPopOver>
    );
};

const CATEGORYTYPES = {
    PROGRAM: 'program',
    ACCESSIBILITY: 'accessibility',
    GENRE: 'genre',
};

const fixEms = (data) => data.replace(/(<em>|<\/em>)/g, '', data);

const mapSearchData = (data) => {
    const accessibility = {
        title: 'Tillgänglighet',
        results: [],
    };

    const categories = {
        title: 'Kategorier',
        results: [],
    };

    const programs = {
        title: 'Program',
        results: [],
    };

    const a11yThumbnail = (id) => {
        switch (id) {
            case 'syntolkat':
                return syntolkat;
            case 'teckensprak':
                return teckensprak;
            case 'upplast-text':
                return upplasttext;
            default:
                return tydligaretal;
        }
    };

    data.forEach((item) => {
        if (item.item.__typename === 'Genre') {
            if (['teckensprak', 'syntolkat', 'tydligare-tal', 'upplast-text'].includes(item.id)) {
                accessibility.results.push({
                    svtId: fixEms(item.name),
                    title: fixEms(item.name),
                    thumbnail: a11yThumbnail(item.id),
                    thumbnailAltText: `${item.name} bild`,
                    url: item.id === 'tydligare-tal' ? `/genre/${item.id}` : `/${item.id}`,
                    isAccessibility: true,
                    category: CATEGORYTYPES.ACCESSIBILITY,
                });
            } else {
                categories.results.push({
                    svtId: fixEms(item.name),
                    title: fixEms(item.name),
                    thumbnail: createImageProxyUrl(
                        item.item.genreImage.id,
                        item.item.genreImage.changed,
                        'wide',
                        140,
                        'quality=90'
                    ),
                    thumbnailAltText: item.item.genreImage.description,
                    isCategory: true,
                    url: `/genre/${item.id}`,
                    popularity: item.item.type === 'Main' ? 2 : 1,
                    category: CATEGORYTYPES.GENRE,
                });
            }
        } else {
            programs.results.push({
                title: fixEms(item.title),
                episodeTitle: item.episodeTitle ? fixEms(item.episodeTitle) : null,
                svtId: item.svtId,
                thumbnail: createImageProxyUrl(
                    item.item.image.id,
                    item.item.image.changed,
                    'wide',
                    140,
                    'quality=90'
                ),
                thumbnailAltText: item.item.image.description,
                popularity: item.item.popularity || 0,
                isCategory: false,
                url: item.item.urls.svtplay,
                badge: item.badge,
                category: CATEGORYTYPES.PROGRAM,
            });
        }
    });

    // Slice to not have more than 8 matches
    return [
        accessibility,
        {
            ...programs,
            results: programs.results.slice(0, 5),
        },
        {
            ...categories,
            results: categories.results.slice(0, 2),
        },
    ];
};

function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        if (value) {
            ref.current = value;
        }
    });

    return ref.current;
}

const AutoCompleteSearch = ({ updateActiveQuery, networkOnline, activeResult, contentoAbTestVariants }) => {
    const router = useRouter();
    const [term, setTerm] = useState('');
    const debouncedTerm = useDebounce(term, 300);

    const ref = useRef(null);

    useEffect(() => {
        if (debouncedTerm) {
            trackCustomEvent(searchQueryEvent(debouncedTerm));
        }
    }, [debouncedTerm]);

    const [result] = useQuery({
        query: SEARCH_QUERY,
        variables: {
            querystring: term.slice(0, 100),
            includeFullOppetArkiv: env.FLAG_ENABLE_ALL_OA,
            abTestVariants: contentoAbTestVariants,
        },
        pause: term === '',
    });

    const { data, fetching, error } = result;

    // We use previousdata to display the previous result while fetching
    // Otherwise the the search box would be blank between fetches
    const previousData = usePrevious(data);
    const previousResult =
        (fetching || error) && previousData?.search ? mapSearchData(previousData.search) : undefined;
    const newResult = data?.search ? mapSearchData(data.search) : undefined;

    const searchResult = fetching || error ? previousResult : newResult;

    const totalSize =
        searchResult && searchResult.length > 0
            ? searchResult[0].results.length + searchResult[1].results.length + searchResult[2].results.length
            : 0;

    const suggestionClick = (url, stats, e) => {
        e.preventDefault();
        if (networkOnline) {
            trackCustomEvent(
                listSearchEvent(
                    {
                        contentId: stats.svtId,
                        listItemIndex: stats.position,
                    },
                    e.currentTarget,
                    stats.type,
                    term
                )
            );
        }

        router.push(url);
        setTerm('');
    };

    const handleChange = (e) => {
        updateActiveQuery(e.target.value);
        setTerm(e.target.value);
    };

    const handleSubmit = (e) => {
        updateActiveQuery('');
        if (ref?.current) {
            ref.current.blur();
        }
        e.preventDefault();

        router.push({
            pathname: '/sok',
            query: { q: term },
        });
    };

    const onAllMatchesClick = (e) => {
        updateActiveQuery('');
        if (ref?.current) {
            ref.current.blur();
        }
    };

    const handleOnFocus = () => {
        updateActiveQuery('');
        updateActiveQuery(term);
    };

    const handleSelect = (searchVal) => {
        // Lookup selection to find url!
        let position = 1;

        const accessibilityItems = searchResult[0].results.find((item, index) => {
            if (item.title === searchVal) {
                position = index + 1;
            }

            return item.title === searchVal;
        });

        const programItems = searchResult[1].results.find((item, index) => {
            // string match for episodes
            if (item.episodeTitle) {
                if (`${item.title}: ${item.episodeTitle}` === searchVal) {
                    position = index + 1;
                }

                return `${item.title}: ${item.episodeTitle}` === searchVal;
            }

            if (item.title === searchVal) {
                position = index + 1;
            }

            return item.title === searchVal;
        });

        const categoryItems = searchResult[2].results.find((item, index) => {
            if (`${item.title} (kategori)` === searchVal) {
                position = index + 1;
            }

            return `${item.title} (kategori)` === searchVal;
        });

        const selectedItem = accessibilityItems || categoryItems || programItems;

        if (selectedItem) {
            trackCustomEvent(
                listSearchEvent(
                    {
                        contentId: selectedItem.svtId,
                        listItemIndex: position,
                    },
                    null,
                    selectedItem.category,
                    term
                )
            );

            router.push(selectedItem.url);
            setTerm('');
        }
    };

    return (
        <Form
            action="/sok"
            method="GET"
            onSubmit={handleSubmit}
            data-rt="autocomplete-search-form"
            autoComplete="off"
        >
            <Combobox
                id="combobox"
                onSelect={(item) => {
                    handleSelect(item);
                }}
                aria-label="Sök. Sökförslag kommer att visas under sökfältet."
            >
                <SearchInput
                    onChange={(e) => {
                        handleChange(e);
                    }}
                    id="search"
                    onFocus={handleOnFocus}
                    placeholder={INPUT_SEARCH_PLACEHOLDER_TEXT}
                    selectOnClick={true}
                    name="q"
                    value={term}
                    ref={ref}
                />
                <SubmitButton type="submit" title="Sök på svtplay.se" aria-label="Sök">
                    <span className="svt_icon svt_icon--search" aria-hidden="true"></span>
                    <SearchLabel>Sök</SearchLabel>
                </SubmitButton>
                {totalSize > 0 && activeResult.get('searchTerm').length > 0 && (
                    <PopOver
                        totalSize={totalSize}
                        result={searchResult}
                        suggestionClick={suggestionClick}
                        term={term}
                        onAllMatchesClick={onAllMatchesClick}
                    />
                )}
            </Combobox>
        </Form>
    );
};

AutoCompleteSearch.propTypes = {
    searchInputActiveOnMobile: PropTypes.bool.isRequired,
    updateActiveQuery: PropTypes.func.isRequired,
    networkOnline: PropTypes.bool,
    contentoAbTestVariants: PropTypes.object,
};

export default AutoCompleteSearch;
